import { DeletedRecord } from "@/Contracts";
import {
	CreateCategoryRequest,
	ModifyCategoryRequest,
	ReorderCategoriesRequest
} from "@/Contracts/Assessments";
import {
	EditQbrDataLogRequest,
	HealthStatusDataLog,
	Qbr
} from "@/Contracts/QBR";
import {
	ChildEntityHydrationParams,
	getQueryParamAuthDetails,
	includeChildEntities
} from "@/Contracts/Utils";
import Axios, { AxiosResponse } from "axios";
import {
	deleteRequest,
	getRequest,
	postRequest,
	putRequest
} from "@/Utils/Service/BackendService";

const getOrEditQbrDetailsUrl = "/v2/qbr/{qbr_id}";
const addQbrDataLogUrl = "/v2/qbr/{qbr_id}/logs";
const editDQbrataLogUrl = "/v2/qbr/{qbr_id}/logs/{assessment_data_log_id}";
const deleteQbrDataLogUrl = "/v2/qbr/{qbr_id}/logs/{assessment_data_log_id}";
const resetRiskScoreUrl = "/v2/qbr/{qbr_id}/score/default";
const createCategoryUrl = "/v2/qbr/{qbr_id}/category/";
const updateCategoryUrl = "/v2/qbr/{qbr_id}/category/{issue_id}/";
const deleteCategoryUrl = "/v2/qbr/{qbr_id}/category_delete/{issue_id}/";
const reorderCategoriesUrl = "/v2/qbr/{qbr_id}/category/reorder/";

export const getQbrDetails = async (
	qbrId: string,
	appendQueryParamAuth: boolean
): Promise<Qbr> => {
	try {
		const includedChildEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.QBR_BLOCKS_ALL
			)
		);
		let url = getOrEditQbrDetailsUrl.replace("{qbr_id}", qbrId);

		const authQueryParam = getQueryParamAuthDetails();
		if (appendQueryParamAuth && !!authQueryParam) {
			url =
				url +
				"?" +
				authQueryParam.queryParam +
				"&" +
				includedChildEntities;
		} else {
			url = url + "?" + includedChildEntities;
		}

		const response: AxiosResponse<Qbr> = await Axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editQbrDetails = async (qbr: Qbr): Promise<Qbr> => {
	try {
		const response: AxiosResponse<Qbr> = await Axios(
			putRequest(
				`${getOrEditQbrDetailsUrl.replace("{qbr_id}", qbr.id)}
				?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.QBR_BLOCKS_ALL
					)
				)}`,
				qbr,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const resetRiskScore = async (qbrId: string): Promise<Qbr> => {
	try {
		const response: AxiosResponse<Qbr> = await Axios(
			putRequest(
				`${resetRiskScoreUrl.replace("{qbr_id}", qbrId)}
				?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.QBR_BLOCKS_ALL
					)
				)}`,
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addQbrDataLog = async (
	qbrDataLog: HealthStatusDataLog,
	qbrId: string
): Promise<HealthStatusDataLog> => {
	try {
		const response: AxiosResponse<HealthStatusDataLog> = await Axios(
			postRequest(
				`${addQbrDataLogUrl.replace("{qbr_id}", qbrId)}
				?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.QBR_BLOCKS_ALL
					)
				)}`,
				qbrDataLog,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editQbrDataLog = async (
	editQbrDataLogRequest: EditQbrDataLogRequest,
	qbrId: string
): Promise<HealthStatusDataLog> => {
	try {
		const response: AxiosResponse<HealthStatusDataLog> = await Axios(
			putRequest(
				`${editDQbrataLogUrl
					.replace("{qbr_id}", qbrId)
					.replace(
						"{assessment_data_log_id}",
						editQbrDataLogRequest.id
					)}
					?${includeChildEntities(
						new Array<ChildEntityHydrationParams>(
							ChildEntityHydrationParams.QBR_BLOCKS_ALL
						)
					)}`,
				editQbrDataLogRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteQbrDataLog = async (
	qbrId: string,
	asesssmentDataLogId: string
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await Axios(
			deleteRequest(
				deleteQbrDataLogUrl
					.replace("{qbr_id}", qbrId)
					.replace("{assessment_data_log_id}", asesssmentDataLogId),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const createCategory = async (
	createCategoryRequest: CreateCategoryRequest
): Promise<Qbr> => {
	try {
		const response: AxiosResponse<Qbr> = await Axios(
			postRequest(
				createCategoryUrl.replace(
					"{qbr_id}",
					createCategoryRequest.entityId
				),
				{ category_name: createCategoryRequest.issueType },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateCategory = async (
	modifyCategoryRequest: ModifyCategoryRequest
): Promise<Qbr> => {
	try {
		const response: AxiosResponse<Qbr> = await Axios(
			putRequest(
				`${updateCategoryUrl
					.replace("{qbr_id}", modifyCategoryRequest.entityId)
					.replace("{issue_id}", modifyCategoryRequest.issueId)}
					?${includeChildEntities(
						new Array<ChildEntityHydrationParams>(
							ChildEntityHydrationParams.QBR_BLOCKS_ALL
						)
					)}`,
				{
					category_name: modifyCategoryRequest.issueType,
					filter_on_status: modifyCategoryRequest.filterOnStatus
				},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteCategory = async (
	modifyCategoryRequest: ModifyCategoryRequest
): Promise<Qbr> => {
	try {
		const response: AxiosResponse<any> = await Axios(
			putRequest(
				`${deleteCategoryUrl
					.replace("{qbr_id}", modifyCategoryRequest.entityId)
					.replace("{issue_id}", modifyCategoryRequest.issueId)}
					?${includeChildEntities(
						new Array<ChildEntityHydrationParams>(
							ChildEntityHydrationParams.QBR_BLOCKS_ALL
						)
					)}`,
				{ category_name: modifyCategoryRequest.issueType },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const reorderCategories = async (
	reorderCategoriesRequest: ReorderCategoriesRequest
): Promise<Qbr> => {
	try {
		const response: AxiosResponse<Qbr> = await Axios(
			putRequest(
				`${reorderCategoriesUrl.replace(
					"{qbr_id}",
					reorderCategoriesRequest.entityId
				)}
				?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.QBR_BLOCKS_ALL
					)
				)}`,
				{ categories: reorderCategoriesRequest.newCategoryList },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
